// ============================================================
// LevelSelect — beautiful level selection screen
// ============================================================
import * as PIXI from 'pixi.js';
import { GAME_WIDTH, GAME_HEIGHT, COLOR } from '../constants.js';
import { LEVELS } from '../data/levels.js';
import { sound } from '../systems/SoundEngine.js';

export class LevelSelect {
  /**
   * @param {PIXI.Container} stage
   * @param {function(number)} onSelect  - called with level index (0-based)
   * @param {function}         onBack
   */
  constructor(stage, onSelect, onBack) {
    this.stage    = stage;
    this.onSelect = onSelect;
    this.onBack   = onBack;
    this.container = new PIXI.Container();
    this.container.zIndex = 200;
    stage.addChild(this.container);
    this._build();
  }

  _build() {
    const c = this.container;

    // ── Dark overlay background ──
    const bg = new PIXI.Graphics();
    bg.rect(0, 0, GAME_WIDTH, GAME_HEIGHT).fill({ color: 0x0A1628 });
    c.addChild(bg);

    // ── Ocean wave pattern ──
    for (let i = 0; i < 6; i++) {
      const wave = new PIXI.Graphics();
      wave.moveTo(0, 0);
      for (let x = 0; x <= GAME_WIDTH; x += 40) {
        wave.lineTo(x, Math.sin(x / 80 + i) * 15);
      }
      wave.lineTo(GAME_WIDTH, 60);
      wave.lineTo(0, 60);
      wave.closePath().fill({ color: 0x0D47A1, alpha: 0.15 + i * 0.04 });
      wave.y = GAME_HEIGHT - 60 - i * 30;
      c.addChild(wave);
    }

    // ── Title ──
    const title = new PIXI.Text({
      text: '🐢 Magic Turtle Words 🐢',
      style: {
        fontSize: 52,
        fontFamily: 'Georgia, serif',
        fill: 0xFFD700,
        fontWeight: 'bold',
        stroke: { color: 0x8B4513, width: 6 },
        dropShadow: { color: 0x000000, blur: 8, distance: 3 },
      },
    });
    title.anchor.set(0.5, 0);
    title.x = GAME_WIDTH / 2;
    title.y = 24;
    c.addChild(title);

    const sub = new PIXI.Text({
      text: 'Type words to solve puzzles!',
      style: { fontSize: 22, fill: 0xB0C4DE, fontStyle: 'italic' },
    });
    sub.anchor.set(0.5, 0);
    sub.x = GAME_WIDTH / 2;
    sub.y = 90;
    c.addChild(sub);

    // ── Level cards ──
    const cardW = 210, cardH = 200;
    const cols  = 3;
    const startX = (GAME_WIDTH - (cols * cardW + (cols - 1) * 24)) / 2 + cardW / 2;
    const row2X  = (GAME_WIDTH - (2 * cardW + 24)) / 2 + cardW / 2;

    LEVELS.forEach((level, i) => {
      const row    = Math.floor(i / cols);
      const col    = i % cols;
      const cx     = row === 1
        ? row2X + col * (cardW + 24)
        : startX + col * (cardW + 24);
      const cy     = 175 + row * (cardH + 26) + cardH / 2;

      const card = this._makeCard(level, cx, cy, cardW, cardH, i);
      c.addChild(card);
    });

    // ── Controls hint ──
    const hint = new PIXI.Text({
      text: '🎮 WASD / Arrow Keys  |  Tab = Word Box  |  Enter = Spawn  |  M = Mute',
      style: { fontSize: 16, fill: 0x607D8B },
    });
    hint.anchor.set(0.5, 1);
    hint.x = GAME_WIDTH / 2;
    hint.y = GAME_HEIGHT - 16;
    c.addChild(hint);
  }

  _makeCard(level, cx, cy, w, h, idx) {
    const card = new PIXI.Container();
    card.x = cx;
    card.y = cy;
    card.interactive = true;
    card.cursor = 'pointer';

    const bgColors = [0x1565C0, 0x00695C, 0x6A1B9A, 0xBF360C, 0x1B5E20];
    const col = bgColors[idx % bgColors.length];

    const bg = new PIXI.Graphics();
    bg.roundRect(-w/2, -h/2, w, h, 16).fill({ color: col, alpha: 0.9 });
    bg.roundRect(-w/2, -h/2, w, h, 16).stroke({ color: 0xFFFFFF, alpha: 0.25, width: 2 });
    // Top highlight
    bg.roundRect(-w/2, -h/2, w, 6, 16).fill({ color: 0xFFFFFF, alpha: 0.15 });
    card.addChild(bg);

    // Level number badge
    const numBadge = new PIXI.Graphics();
    numBadge.circle(0, 0, 24).fill({ color: 0xFFD700 });
    numBadge.x = -w/2 + 24;
    numBadge.y = -h/2 + 24;
    card.addChild(numBadge);
    const numText = new PIXI.Text({ text: `${level.id}`, style: { fontSize: 20, fill: 0x1A1A2E, fontWeight: 'bold' } });
    numText.anchor.set(0.5);
    numText.x = numBadge.x;
    numText.y = numBadge.y;
    card.addChild(numText);

    // Stars
    const starsText = new PIXI.Text({
      text: '⭐'.repeat(level.stars) + '☆'.repeat(3 - level.stars),
      style: { fontSize: 18 },
    });
    starsText.anchor.set(1, 0);
    starsText.x = w/2 - 8;
    starsText.y = -h/2 + 10;
    card.addChild(starsText);

    // Name
    const nameText = new PIXI.Text({
      text: level.name,
      style: { fontSize: 20, fill: 0xFFFFFF, fontWeight: 'bold', wordWrap: true, wordWrapWidth: w - 20, align: 'center' },
    });
    nameText.anchor.set(0.5, 0);
    nameText.x = 0;
    nameText.y = -h/2 + 50;
    card.addChild(nameText);

    // Subtitle
    const subText = new PIXI.Text({
      text: level.subtitle,
      style: { fontSize: 13, fill: 0xB0BEC5, fontStyle: 'italic', wordWrap: true, wordWrapWidth: w - 24, align: 'center' },
    });
    subText.anchor.set(0.5, 0);
    subText.x = 0;
    subText.y = -h/2 + 90;
    card.addChild(subText);

    // Hint words
    const hintsText = new PIXI.Text({
      text: level.hints.slice(0, 3).join(' · '),
      style: { fontSize: 13, fill: 0xFFD54F, fontStyle: 'italic', align: 'center' },
    });
    hintsText.anchor.set(0.5, 1);
    hintsText.x = 0;
    hintsText.y = h/2 - 40;
    card.addChild(hintsText);

    // Play button
    const btn = new PIXI.Graphics();
    btn.roundRect(-55, -18, 110, 36, 18).fill({ color: 0x4CAF50 });
    btn.roundRect(-55, -18, 110, 36, 18).stroke({ color: 0x2E7D32, width: 2 });
    const btnText = new PIXI.Text({ text: '▶  PLAY', style: { fontSize: 17, fill: 0xFFFFFF, fontWeight: 'bold' } });
    btnText.anchor.set(0.5);
    btn.y = h/2 - 20;
    btnText.y = h/2 - 20;
    card.addChild(btn);
    card.addChild(btnText);

    // Hover effects
    card.on('pointerover', () => {
      card.scale.set(1.05);
      bg.tint = 0xDDEEFF;
      sound.init();
    });
    card.on('pointerout', () => {
      card.scale.set(1.0);
      bg.tint = 0xFFFFFF;
    });
    card.on('pointertap', () => {
      sound.buttonClick();
      this.onSelect?.(idx);
    });

    return card;
  }

  show() { this.container.visible = true; }
  hide() { this.container.visible = false; }
  destroy() {
    if (this.container.parent) this.container.parent.removeChild(this.container);
    this.container.destroy();
  }
}
